import { Controller } from "@hotwired/stimulus"

// Recurring schedule form controller
//
// Handles:
// - Pet selection
// - Day selection
// - Frequency toggle
// - Date range
// - Transport toggle
// - Live preview
// - Form validation
//
export default class extends Controller {
  static targets = [
    // Pet selection
    "petsContainer",
    "petCard",
    "petCheckbox",
    "petCountDisplay",
    // Day selection
    "daysContainer",
    "dayButton",
    "dayCheckbox",
    "daysCountDisplay",
    // Frequency
    "frequencyContainer",
    "frequencyRadio",
    // Date range
    "startDate",
    "endConditionRadio",
    "endDateContainer",
    "endDate",
    // Transport
    "transportCheckbox",
    "transportAddressContainer",
    "transportAddressSelect",
    // Preview
    "previewContainer",
    "patternPreview",
    "datesPreview",
    "passBalance",
    "creditsPerPeriod",
    // Form
    "form",
    "submitButton"
  ]

  static values = {
    passes: { type: Number, default: 0 }
  }

  connect() {
    this.updatePreview()
    this.validateForm()
  }

  // Pet selection
  updatePetSelection(event) {
    const checkbox = event.target
    const petId = checkbox.value
    const isChecked = checkbox.checked

    // Find the corresponding pet card and update its visual state
    const petCard = this.petCardTargets.find(card => card.dataset.petId === petId)
    if (petCard) {
      const checkIcon = petCard.querySelector('.pet-check-icon')
      if (isChecked) {
        petCard.classList.remove('border-gray-200', 'hover:border-gray-300')
        petCard.classList.add('border-[var(--color-primary)]', 'bg-[var(--color-primary)]/5')
        if (checkIcon) checkIcon.style.display = 'inline'
      } else {
        petCard.classList.add('border-gray-200', 'hover:border-gray-300')
        petCard.classList.remove('border-[var(--color-primary)]', 'bg-[var(--color-primary)]/5')
        if (checkIcon) checkIcon.style.display = 'none'
      }
    }

    this.updatePetCountDisplay()
    this.updatePreview()
    this.validateForm()
  }

  updatePetCountDisplay() {
    if (!this.hasPetCountDisplayTarget) return

    const count = this.getSelectedPetCount()
    if (count === 0) {
      this.petCountDisplayTarget.textContent = 'Select at least one pet'
      this.petCountDisplayTarget.classList.add('text-amber-600')
      this.petCountDisplayTarget.classList.remove('text-gray-500')
    } else {
      this.petCountDisplayTarget.textContent = `${count} pet${count > 1 ? 's' : ''} selected`
      this.petCountDisplayTarget.classList.remove('text-amber-600')
      this.petCountDisplayTarget.classList.add('text-gray-500')
    }
  }

  getSelectedPetCount() {
    if (!this.hasPetCheckboxTarget) return 0
    return this.petCheckboxTargets.filter(cb => cb.checked).length
  }

  // Day selection
  updateDaySelection(event) {
    const checkbox = event.target
    const day = checkbox.value
    const isChecked = checkbox.checked

    // Find the corresponding day button and update its visual state
    const dayButton = this.dayButtonTargets.find(btn => btn.dataset.day === day)
    if (dayButton) {
      if (isChecked) {
        dayButton.classList.remove('border-gray-200', 'hover:border-gray-300', 'text-gray-600')
        dayButton.classList.add('border-[var(--color-primary)]', 'bg-[var(--color-primary)]', 'text-white')
      } else {
        dayButton.classList.add('border-gray-200', 'hover:border-gray-300', 'text-gray-600')
        dayButton.classList.remove('border-[var(--color-primary)]', 'bg-[var(--color-primary)]', 'text-white')
      }
    }

    this.updateDaysCountDisplay()
    this.updatePreview()
    this.validateForm()
  }

  updateDaysCountDisplay() {
    if (!this.hasDaysCountDisplayTarget) return

    const count = this.getSelectedDaysCount()
    if (count === 0) {
      this.daysCountDisplayTarget.textContent = 'Select at least one day'
      this.daysCountDisplayTarget.classList.add('text-amber-600')
      this.daysCountDisplayTarget.classList.remove('text-gray-500')
    } else {
      this.daysCountDisplayTarget.textContent = `${count} day${count > 1 ? 's' : ''} per ${this.getFrequency() === 'bi_weekly' ? '2 weeks' : 'week'}`
      this.daysCountDisplayTarget.classList.remove('text-amber-600')
      this.daysCountDisplayTarget.classList.add('text-gray-500')
    }
  }

  getSelectedDays() {
    if (!this.hasDayCheckboxTarget) return []
    return this.dayCheckboxTargets.filter(cb => cb.checked).map(cb => cb.value)
  }

  getSelectedDaysCount() {
    return this.getSelectedDays().length
  }

  // Frequency
  updateFrequency() {
    // Update visual state of frequency options
    this.frequencyRadioTargets.forEach(radio => {
      const option = radio.closest('label').querySelector('.frequency-option')
      if (radio.checked) {
        option.classList.add('bg-[var(--color-primary)]', 'text-white')
        option.classList.remove('text-gray-600', 'hover:bg-gray-50')
      } else {
        option.classList.remove('bg-[var(--color-primary)]', 'text-white')
        option.classList.add('text-gray-600', 'hover:bg-gray-50')
      }
    })

    this.updateDaysCountDisplay()
    this.updatePreview()
  }

  getFrequency() {
    const checked = this.frequencyRadioTargets.find(r => r.checked)
    return checked?.value || 'weekly'
  }

  // End condition
  updateEndCondition() {
    const endCondition = this.endConditionRadioTargets.find(r => r.checked)?.value

    if (this.hasEndDateContainerTarget) {
      if (endCondition === 'end_date') {
        this.endDateContainerTarget.classList.remove('hidden')
      } else {
        this.endDateContainerTarget.classList.add('hidden')
        // Clear the end date when switching to ongoing
        if (this.hasEndDateTarget) {
          this.endDateTarget.value = ''
        }
      }
    }

    this.updatePreview()
  }

  // Transport
  toggleTransport() {
    if (!this.hasTransportAddressContainerTarget) return

    if (this.transportCheckboxTarget.checked) {
      this.transportAddressContainerTarget.classList.remove('hidden')
    } else {
      this.transportAddressContainerTarget.classList.add('hidden')
    }
  }

  // Preview
  updatePreview() {
    this.updatePatternPreview()
    this.updateDatesPreview()
    this.updateCreditsPreview()
  }

  updatePatternPreview() {
    if (!this.hasPatternPreviewTarget) return

    const days = this.getSelectedDays()
    if (days.length === 0) {
      this.patternPreviewTarget.textContent = 'Select days to see pattern'
      return
    }

    const dayNames = {
      sunday: 'Sun',
      monday: 'Mon',
      tuesday: 'Tue',
      wednesday: 'Wed',
      thursday: 'Thu',
      friday: 'Fri',
      saturday: 'Sat'
    }

    const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const sortedDays = days.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b))
    const dayStr = sortedDays.map(d => dayNames[d]).join(', ')
    const freqStr = this.getFrequency() === 'bi_weekly' ? 'every 2 weeks' : 'weekly'

    this.patternPreviewTarget.textContent = `${dayStr} ${freqStr}`
  }

  // Parse YYYY-MM-DD as local date to avoid UTC vs local time issues
  parseLocalDate(dateStr) {
    if (!dateStr) return new Date()
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  updateDatesPreview() {
    if (!this.hasDatesPreviewTarget) return

    const days = this.getSelectedDays()
    if (days.length === 0) {
      this.datesPreviewTarget.textContent = '--'
      return
    }

    const startDate = this.hasStartDateTarget ? this.parseLocalDate(this.startDateTarget.value) : new Date()
    if (isNaN(startDate.getTime())) {
      this.datesPreviewTarget.textContent = '--'
      return
    }

    const frequency = this.getFrequency()
    const intervalWeeks = frequency === 'bi_weekly' ? 2 : 1

    // Generate next 4 dates
    const dates = this.generateNextDates(startDate, days, intervalWeeks, 4)

    if (dates.length === 0) {
      this.datesPreviewTarget.textContent = '--'
      return
    }

    const formatter = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    this.datesPreviewTarget.textContent = dates.map(d => formatter.format(d)).join(', ')
  }

  generateNextDates(startDate, days, intervalWeeks, count) {
    const dayMap = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6
    }

    const targetDays = days.map(d => dayMap[d]).sort((a, b) => a - b)
    const dates = []
    const current = new Date(startDate)
    let weekNum = 0

    // Look up to 52 weeks ahead
    for (let week = 0; week < 52 && dates.length < count; week++) {
      // Only process dates in valid weeks (every week or every other week)
      if (weekNum % intervalWeeks === 0) {
        for (const targetDay of targetDays) {
          if (dates.length >= count) break

          // Find the day in this week
          const dayOfWeek = current.getDay()
          const daysUntilTarget = (targetDay - dayOfWeek + 7) % 7
          const targetDate = new Date(current)
          targetDate.setDate(current.getDate() + daysUntilTarget)

          // Only add if it's on or after the start date
          if (targetDate >= startDate) {
            dates.push(new Date(targetDate))
          }
        }
      }

      // Move to next week
      current.setDate(current.getDate() + 7)
      weekNum++
    }

    return dates.slice(0, count)
  }

  updateCreditsPreview() {
    if (!this.hasCreditsPerPeriodTarget) return

    const daysCount = this.getSelectedDaysCount()
    const petsCount = this.getSelectedPetCount()
    const creditsPerPeriod = daysCount * petsCount

    const period = this.getFrequency() === 'bi_weekly' ? '2 weeks' : 'week'
    this.creditsPerPeriodTarget.textContent = `${creditsPerPeriod} passes/${period}`
  }

  // Validation
  validateForm() {
    if (!this.hasSubmitButtonTarget) return

    const hasSelectedPets = this.getSelectedPetCount() > 0
    const hasSelectedDays = this.getSelectedDaysCount() > 0
    const hasPasses = this.passesValue > 0
    const hasTransportAddress = !this.hasTransportCheckboxTarget ||
      !this.transportCheckboxTarget.checked ||
      (this.hasTransportAddressSelectTarget && this.transportAddressSelectTarget.value)

    const isValid = hasSelectedPets && hasSelectedDays && hasPasses && hasTransportAddress

    this.submitButtonTarget.disabled = !isValid
  }
}
